/* eslint-disable import/no-anonymous-default-export */
import React, { useEffect, useState, useMemo } from "react";
import styled from "styled-components";
import * as Yup from "yup";
import Axios from "axios";
import { useModalContext } from "../../../components/modal";
import { useSnackbarContext } from "../../../components/snackbar";
import { mapValues } from "lodash";
import { format } from "date-fns";
import { Input } from "components/input";
import { Button, NoStyleButton, OutlineButton } from "components/button";
import AssigneeSelect from "components/react-select/assignees";
import { theme } from "styles/theme";
import Switch from "rc-switch";
import DurationInput from "components/input/duration";
import { durationStrToMinutes, parseDuration } from "utils/duration";
import { useFormik } from "formik";
import Field from "components/field";
import GroupedButton from "components/button/grouped";
import AppointmentType from "components/react-select/appointment-type";
import LineItemsContainer from "containers/lineItems";
import { useMutation, useQuery, useQueryClient } from "react-query";
import apiAxios from "api/axios";
import { VscWarning } from "react-icons/vsc";
import DatePicker from "react-multi-date-picker";
import axios from "axios";
import { useTranslation } from "react-i18next";
import { enUS, es } from "date-fns/locale";
import { convertFromUTC, convertToUTC } from "utils/utcConversion";
import { formatInTimeZone } from "date-fns-tz";
import { useAuthContext } from "contexts/auth";
import {
  isIsoDateWithinSupportedRange,
  parseIsoDateWithinRange,
} from "utils/dateValidation";
import { IoMdInformationCircleOutline } from "react-icons/io";
import { CgUndo } from "react-icons/cg";
import TooltippedElement from "components/TooltippedElement";
import { AppointmentModalChecklistsSection } from "./AppointmentModalChecklistsSection";

const AppointmentModal = styled.form`
  display: flex;
  flex-direction: column;
  width: 100vw;
  max-width: 1000px;
  overflow-x: none;
`;

const Row = styled.div`
  margin: 0.5rem 0;
  display: grid;
  align-items: center;
  grid-template-columns: 1fr 3fr;
  gap: 1rem;
  > label {
    text-align: right;
    font-weight: bold;
    font-size: 0.8rem;
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: flex-end;
    gap: 0.25rem;
  }
`;

const TimeCon = styled.div`
  display: flex;
  align-items: center;
  margin: 0.5rem 0;
  > div {
    flex: 1;
  }
`;

const getAppointmentEmailConfig = async () => {
  const res = await axios.get(`${process.env.REACT_APP_SERVER_URL}/api/v1/company/appointment/emailConfirmation`);
  return res.data;
};

const isNonEmpty = (val) => {
  if (val === null || val === undefined || val === "" || val === false) return false;
  if (Array.isArray(val) && val.length === 0) return false;
  return true;
};

/** Same URL shape as LineItemsContainer products fetch (must match its react-query key). */
const buildLineItemsProductsUrl = (lineItemsType, lineItemsId, parentId) => {
  const API_BASE = `${process.env.REACT_APP_SERVER_URL}/api/v1/company`;
  const params = new URLSearchParams();
  if (parentId != null) params.set("parent_id", parentId);
  const qs = params.toString();
  return `${API_BASE}/products/${lineItemsType}/${lineItemsId}${qs ? `?${qs}` : ""}`;
};

const toApiDurationString = (duration) => {
  if (duration == null || duration === "") {
    return null;
  }
  if (typeof duration === "number") {
    return String(Math.round(duration)).padStart(4, "0");
  }
  const mins = durationStrToMinutes(String(duration));
  if (mins == null) {
    return null;
  }
  return parseDuration(mins).fullStr;
};

export default function ({
  parentId = null,
  parentType = null,
  id,
  type,
  closed,
  appointmentId,
  appointment,
  acceptedBid = null,
  emailDefault,
  has_est = false,
  defaultdate,
  isBookit = false,
  responses = [],
}) {
  const { t, i18n } = useTranslation();
  const tBase = "containers.appointmentCard.appointment-folder.appointmentModal";
  const tr = (key) => t(`${tBase}.${key}`);
  const invalidDateMessage = tr("Please enter a valid date (YYYY-MM-DD).");

  const { setModal } = useModalContext();
  const { showSnackbar } = useSnackbarContext();
  const { timezone } = useAuthContext();
  const effectiveTz = timezone || Intl.DateTimeFormat().resolvedOptions().timeZone;

  const [isEstimating, setIsEstimating] = React.useState(false);
  const [sendEmail, setSendEmail] = useState(emailDefault);
  const [isFlyoutVisible, setIsFlyoutVisible] = useState(false);
  const checklistSectionRef = React.useRef(null);
  const [appointmentEmailConfig, setAppointmentEmailConfig] = useState();
  const [selectedTypeId, setSelectedTypeId] = useState(appointment?.fk_appointment_type_id?.toString() || "");
  const [selectedDefaultId, setSelectedDefaultId] = useState(
    appointment?.fk_appointment_type_default_id?.toString() || "new"
  );

  const [valuesBeforeDefaultFill, setValuesBeforeDefaultFill] = useState(null);
  const [defaultModifiedFields, setDefaultModifiedFields] = useState(new Set());
  const [apptTypeLineItemSource, setApptTypeLineItemSource] = useState(null);
  const [apptTypeItemTargetId, setApptTypeItemTargetId] = useState(null);
  const [pendingApptTypeLineItems, setPendingApptTypeLineItems] = useState(null);
  const [apptTypeLineItemsAdded, setApptTypeLineItemsAdded] = useState(false);
  const [isLineItemsPrefetching, setIsLineItemsPrefetching] = useState(false);
  const [checklistDisplayCount, setChecklistDisplayCount] = useState(0);

  const queryClient = useQueryClient();

  const isProgrammaticDefaultChange = React.useRef(false);
  const lastAppliedChildDefaultRef = React.useRef(null);

  useEffect(() => {
    const appointmentTypeId = appointment?.fk_appointment_type_id?.toString() || "";
    let appointmentDefaultId = "new";
    if (appointment?.fk_appointment_type_default_id) {
      appointmentDefaultId = appointment.fk_appointment_type_default_id.toString();
    } else if (appointmentTypeId) {
      appointmentDefaultId = "parent-applied";
    }
    setSelectedTypeId(appointmentTypeId);
    setSelectedDefaultId(appointmentDefaultId);
    lastAppliedChildDefaultRef.current =
      appointmentDefaultId === "new" || appointmentDefaultId === "parent-applied"
        ? null
        : appointmentDefaultId;
  }, [
    appointment?.id,
    appointment?.fk_appointment_type_id,
    appointment?.fk_appointment_type_default_id,
  ]);

  const getEmployeeNames = async (employeeIds) => {
    const employees = await Axios.get(`${process.env.REACT_APP_SERVER_URL}/api/v1/company/employee/names`,
      { params: { employeeIds: employeeIds } }
    );
    return employees.data;
  };

  const buildDefaultChanges = async (source) => {
    const changes = {};
    const changedFields = new Set(); // keep track of fields that have been changed

    console.log("source", source);

    if (source.duration != null) {
      changes.duration = parseDuration(source.duration).fullStr || "";
      changedFields.add("duration");
    }
    if (source.description != null) {
      changes.note = source.description;
      changedFields.add("note");
    }
    if (source.assigned_to != null && source.assigned_to !== "") {
      // Get employee names
      const employeeNames = await getEmployeeNames(source.assigned_to);
      if (employeeNames.length) {
        const newAssignees = employeeNames.map((e) => (
          { value: e.id, label: `${e.first_name} ${e.last_name}` }
        ))
        changes.assignee = newAssignees;
        changedFields.add("assignee");
      }
    }
    if (source.completion_note != null) {
      changes.completion_note = source.completion_note;
      changedFields.add("completion_note");
    }
    if (source.date) {
      changes.date = String(source.date).split("T")[0];
      changedFields.add("date");
    }
    if (source.start_time_utc) {
      try {
        changes.start_time_utc = formatInTimeZone(
          new Date(source.start_time_utc),
          effectiveTz,
          "HH:mm"
        );
      } catch {
        changes.start_time_utc = convertFromUTC(source.start_time_utc, timezone, false);
      }
      changedFields.add("start_time_utc");
    }
    if (source.end_time_utc) {
      try {
        changes.end_time_utc = formatInTimeZone(
          new Date(source.end_time_utc),
          effectiveTz,
          "HH:mm"
        );
      } catch {
        changes.end_time_utc = convertFromUTC(source.end_time_utc, timezone, false);
      }
      changedFields.add("end_time_utc");
    }
    if (source.category) {
      changes.type = source.category === "estimate" ? "estimate" : "job";
      changedFields.add("type");
    }
    if (source.send_reminder !== undefined && source.send_reminder !== null) {
      changes.is_reminding = !!source.send_reminder;
      changedFields.add("is_reminding");
    } else if (source.send_reminder === null) {
      changes.is_reminding = false;
      changedFields.add("is_reminding");
    }

    return { changes, changedFields };
  };

  const fieldValuesEqualForUndo = (fieldName, previousVal, incomingVal) => {
    if (previousVal === incomingVal) return true;
    if (previousVal == null && incomingVal == null) return true;
    if (fieldName === "assignee") {
      const idsFrom = (arr) =>
        (Array.isArray(arr) ? arr : [])
          .map((x) => x?.value)
          .filter((x) => x != null)
          .slice()
          .sort((x, y) => Number(x) - Number(y));
      const a = idsFrom(previousVal);
      const b = idsFrom(incomingVal);
      if (a.length !== b.length) return false;
      return a.every((id, i) => String(id) === String(b[i]));
    }
    if (typeof previousVal === "boolean" || typeof incomingVal === "boolean") {
      return Boolean(previousVal) === Boolean(incomingVal);
    }
    const prevStr = previousVal == null || previousVal === "" ? "" : String(previousVal).trim();
    const nextStr = incomingVal == null || incomingVal === "" ? "" : String(incomingVal).trim();
    return prevStr === nextStr;
  };

  // Show undo only when a non-empty prior value would be overwritten by a *different* default.
  const computeModifiedFields = (changedFields, previousValues, changes) => {
    const modified = new Set();
    for (const field of changedFields) {
      if (!Object.prototype.hasOwnProperty.call(changes, field)) continue;
      if (!isNonEmpty(previousValues[field])) continue;
      if (fieldValuesEqualForUndo(field, previousValues[field], changes[field])) continue;
      modified.add(field);
    }
    return modified;
  };

  const appointmentTypesQuery = useQuery(
    "appointment-types-with-defaults-modal",
    async () =>
      await axios
        .get(`${process.env.REACT_APP_SERVER_URL}/api/v1/company/appointment-types/with-defaults`)
        .then((res) => res.data),
    { staleTime: 60000 }
  );

  const selectedType = useMemo(() => {
    if (!selectedTypeId || !appointmentTypesQuery.data) return null;
    return appointmentTypesQuery.data.find((t) => String(t.id) === String(selectedTypeId));
  }, [selectedTypeId, appointmentTypesQuery.data]);

  const pendingLineItemsProductsUrl = useMemo(() => {
    if (!pendingApptTypeLineItems) return null;
    const { lineItemsType, lineItemsId, parentId } = pendingApptTypeLineItems;
    const params = new URLSearchParams();
    if (parentId != null) params.set("parent_id", parentId);
    const qs = params.toString();
    return `${process.env.REACT_APP_SERVER_URL}/api/v1/company/products/${lineItemsType}/${lineItemsId}${qs ? `?${qs}` : ""}`;
  }, [pendingApptTypeLineItems]);

  const pendingLineItemsCountQuery = useQuery(
    [
      "appointment-modal-pending-line-items-count",
      pendingApptTypeLineItems?.lineItemsType,
      pendingApptTypeLineItems?.lineItemsId,
      pendingApptTypeLineItems?.parentId,
    ],
    async () => {
      const res = await axios.get(pendingLineItemsProductsUrl);
      const products = res.data?.products;
      return Array.isArray(products) ? products.length : 0;
    },
    {
      enabled: Boolean(pendingApptTypeLineItems && pendingLineItemsProductsUrl),
      staleTime: 30000,
    }
  );

  const pendingLineItemsOfferCount = pendingLineItemsCountQuery.data ?? 0;

  const checklistDefaultsCountQuery = useQuery(
    [
      "appointment-modal-checklist-defaults-count",
      selectedTypeId,
      selectedDefaultId,
    ],
    async () => {
      const params = new URLSearchParams();
      params.set("fk_appointment_type_id", String(selectedTypeId));
      const d = String(selectedDefaultId);
      if (d && d !== "new" && d !== "parent-applied") {
        params.set("fk_appointment_type_default_id", d);
      }
      const res = await axios.get(
        `${process.env.REACT_APP_SERVER_URL}/api/v1/company/appointment-checklists/defaults-count?${params.toString()}`
      );
      return typeof res.data?.count === "number" ? res.data.count : 0;
    },
    {
      enabled: Boolean(selectedTypeId),
      staleTime: 30000,
    }
  );

  const defaultOptions = useMemo(() => {
    const localTr = (key) => t(`${tBase}.${key}`);
    const options = [
      { value: "new", label: localTr("(No auto-fill)") },
    ];

    if (selectedType) {
      const activeDefaults = (selectedType.defaults || []).filter((d) => d.active);

      if (activeDefaults.length > 0) {
        options.push({
          value: "parent-applied",
          label: "(Default)",
        });
        activeDefaults.forEach((d) => {
          options.push({
            value: d.id.toString(),
            label: d.label || localTr("Unnamed Default"),
          });
        });
      }
    }

    return options;
  }, [selectedType, t, tBase]);

  const selectedDefaultOption = useMemo(
    () => defaultOptions.find((opt) => String(opt.value) === String(selectedDefaultId)),
    [defaultOptions, selectedDefaultId]
  );

  const getAppointmentEmailConfigMutation = useMutation(
    getAppointmentEmailConfig,
    {
      onSuccess: (data) => {
        setAppointmentEmailConfig(data);
      },
    }
  );

  const createNewAppt = useMutation(
    async (e) =>
      await Axios.post(
        `${process.env.REACT_APP_SERVER_URL}/api/v1/company/appointment/${type}/${id}`,
        e
      ).then((res) => res.data),
    {
      onSuccess: async (res) => {
        try {
          if (res?.id && checklistSectionRef.current?.flushPending) {
            await checklistSectionRef.current.flushPending(Number(res.id));
          }
        } catch (err) {
          console.error(err);
          showSnackbar(tr("Checklists flush error"));
        }
        if (res?.id) {
          queryClient.invalidateQueries(["products", String(res.id), "appointment"]);
        }
        showSnackbar(tr("Appointment Added!"));
        closed();
        setModal();
        return res;
      },
    }
  );

  const updateAppt = useMutation(
    async (e) =>
      await Axios.post(
        `${process.env.REACT_APP_SERVER_URL}/api/v1/company/appointment/${appointmentId}/update`,
        e
      ).then((res) => res.data),
    {
      onSuccess: async () => {
        try {
          if (appointmentId && checklistSectionRef.current?.flushPending) {
            await checklistSectionRef.current.flushPending(Number(appointmentId));
          }
        } catch (err) {
          console.error(err);
          showSnackbar(tr("Checklists flush error"));
        }
        if (appointmentId) {
          queryClient.invalidateQueries(["products", String(appointmentId), "appointment"]);
        }
        showSnackbar(tr("Appointment Updated!"));
        closed();
        setModal();
      },
    }
  );

  const handleShowRequestData = () => {
    setIsFlyoutVisible(!isFlyoutVisible);
  };

  const syncLineItemsOfferFromSource = (source) => {
    const targetId = source.item_target_id != null && source.item_target_id !== ""
      ? source.item_target_id
      : null;
    if (!targetId) {
      setPendingApptTypeLineItems(null);
      setApptTypeLineItemsAdded(false);
      setApptTypeLineItemSource(null);
      setApptTypeItemTargetId(null);
      return;
    }
    const isChild = source.fk_appointment_type_id != null && source.fk_appointment_type_id !== "";
    setPendingApptTypeLineItems({
      itemTargetId: targetId,
      lineItemsType: isChild ? "appointment-type-default" : "appointment-type",
      lineItemsId: String(source.id),
      parentId: isChild ? String(source.fk_appointment_type_id) : undefined,
    });
    setApptTypeLineItemsAdded(false);
    setApptTypeLineItemSource(null);
    setApptTypeItemTargetId(null);
  };

  const handleAddAppointmentTypeLineItems = async () => {
    if (!pendingApptTypeLineItems || isLineItemsPrefetching) return;
    const { lineItemsType, lineItemsId, parentId, itemTargetId } = pendingApptTypeLineItems;

    setIsLineItemsPrefetching(true);
    try {
      if (appointment && appointmentId) {
        const productsUrl = buildLineItemsProductsUrl(lineItemsType, lineItemsId, parentId);
        const sourceProductsData = await queryClient.fetchQuery(
          ["products", lineItemsId, lineItemsType],
          async () => (await apiAxios.get(productsUrl)).data,
          { staleTime: 30000 }
        );
        const sourceProducts = Array.isArray(sourceProductsData?.products)
          ? sourceProductsData.products
          : [];

        const appointmentQueryKey = ["products", String(appointmentId), "appointment"];
        const existingAppointmentProductsData =
          queryClient.getQueryData(appointmentQueryKey) ||
          (await queryClient.fetchQuery(
            appointmentQueryKey,
            async () =>
              (
                await apiAxios.get(
                  `${process.env.REACT_APP_SERVER_URL}/api/v1/company/products/appointment/${appointmentId}`
                )
              ).data
          ));
        const existingProducts = Array.isArray(existingAppointmentProductsData?.products)
          ? existingAppointmentProductsData.products
          : [];
        const maxOrdering = existingProducts.length
          ? Math.max(...existingProducts.map((product) => Number(product?.ordering) || 0))
          : 0;
        const importedGroupIds = new Map();
        let importedProductId = -1;
        const importedProducts = sourceProducts.map((product, idx) => {
          const groupId = product?.fk_ingroup_id;
          if (groupId != null && !importedGroupIds.has(groupId)) {
            importedGroupIds.set(
              groupId,
              `group_${groupId}_import_${Date.now()}_${importedGroupIds.size + 1}`
            );
          }
          const nextProduct = {
            ...product,
            id: importedProductId,
            new: true,
            ordering: maxOrdering + idx + 1,
          };
          importedProductId -= 1;
          if (groupId != null) {
            nextProduct.group_instance_id = importedGroupIds.get(groupId);
          }
          return nextProduct;
        });

        queryClient.setQueryData(appointmentQueryKey, {
          ...(existingAppointmentProductsData || {}),
          products: [...existingProducts, ...importedProducts],
        });
        setPendingApptTypeLineItems(null);
        setApptTypeLineItemsAdded(true);
        setApptTypeLineItemSource(null);
        setApptTypeItemTargetId(null); // avoid re-importing at submit; items are now in local edit state
        showSnackbar(tr("Line items added in edit mode"));
        return;
      }
      const productsUrl = buildLineItemsProductsUrl(lineItemsType, lineItemsId, parentId);
      const lineItemsQueryKey = ["products", lineItemsId, lineItemsType];
      await queryClient.prefetchQuery(
        lineItemsQueryKey,
        async () => (await apiAxios.get(productsUrl)).data,
        { staleTime: 30000 }
      );
      setApptTypeLineItemSource({
        lineItemsType,
        lineItemsId,
        parentId,
      });
      setApptTypeItemTargetId(itemTargetId);
      setApptTypeLineItemsAdded(true);
      setPendingApptTypeLineItems(null);
    } catch (err) {
      console.error(err);
      showSnackbar(tr("Line items import failed"));
    } finally {
      setIsLineItemsPrefetching(false);
    }
  };

  const handleAppointmentLineItemsCancel = () => {
    if (!appointmentId) return;
    const appointmentQueryKey = ["products", String(appointmentId), "appointment"];
    queryClient.removeQueries(appointmentQueryKey, { exact: true });
    queryClient.invalidateQueries(appointmentQueryKey);
    setApptTypeLineItemsAdded(false);
  };

  const applySource = async (source, currentFormikValues, currentSendEmail, extraFormPatch = {}) => {
    const { changes, changedFields } = await buildDefaultChanges(source);

    setValuesBeforeDefaultFill({
      ...currentFormikValues,
      _sendEmail: currentSendEmail,
    });

    const modified = computeModifiedFields(changedFields, currentFormikValues, changes);
    setDefaultModifiedFields(modified);

    formik.setValues({
      ...currentFormikValues,
      ...extraFormPatch,
      ...changes,
    });

    if (source.email_confirmation !== null && source.email_confirmation !== undefined) {
      setSendEmail(!!source.email_confirmation);
    }

    syncLineItemsOfferFromSource(source);
  };

  const handleTypeChange = async (e) => {
    const newTypeId = e?.value || "";
    const previousTypeId = selectedTypeId;

    if (!newTypeId) {
      lastAppliedChildDefaultRef.current = null;
      setSelectedTypeId("");
      if (valuesBeforeDefaultFill) {
        const { _sendEmail, ...prev } = valuesBeforeDefaultFill;
        formik.setValues((cur) => ({
          ...cur,
          ...prev,
          fk_appointment_type_id: "",
          fk_appointment_type_default_id: null,
        }));
        if (_sendEmail !== undefined) setSendEmail(_sendEmail);
      } else {
        formik.setValues((prev) => ({
          ...prev,
          fk_appointment_type_id: "",
          fk_appointment_type_default_id: null,
        }));
      }
      setSelectedDefaultId("new");
      setDefaultModifiedFields(new Set());
      setPendingApptTypeLineItems(null);
      setApptTypeLineItemsAdded(false);
      setApptTypeItemTargetId(null);
      setApptTypeLineItemSource(null);
      setValuesBeforeDefaultFill(null);
      return;
    }

    if (String(newTypeId) === String(previousTypeId)) {
      setSelectedTypeId(newTypeId);
      formik.setValues((prev) => ({ ...prev, fk_appointment_type_id: String(newTypeId) }));
      return;
    }

    lastAppliedChildDefaultRef.current = null;
    setSelectedTypeId(newTypeId);

    const newType = (appointmentTypesQuery.data || []).find(
      (t) => String(t.id) === String(newTypeId)
    );

    if (!newType) {
      formik.setValues((prev) => ({
        ...prev,
        fk_appointment_type_id: String(newTypeId),
        fk_appointment_type_default_id: null,
      }));
      setSelectedDefaultId("new");
      return;
    }

    const hasParentDefaults =
      newType.description ||
      newType.duration ||
      newType.assigned_to ||
      newType.completion_note ||
      newType.date ||
      newType.start_time_utc ||
      newType.end_time_utc ||
      newType.category ||
      newType.send_reminder !== undefined ||
      newType.email_confirmation != null;

    if (hasParentDefaults || newType.item_target_id) {
      const currentVals = { ...formik.values, fk_appointment_type_id: String(newTypeId) };
      await applySource(
        newType,
        currentVals,
        sendEmail,
        {
          fk_appointment_type_id: String(newTypeId),
          fk_appointment_type_default_id: null,
        }
      );

      const activeDefaults = (newType.defaults || []).filter((d) => d.active);
      if (activeDefaults.length > 0) {
        isProgrammaticDefaultChange.current = true;
        setSelectedDefaultId("parent-applied");
      } else {
        setSelectedDefaultId("new");
      }
    } else {
      formik.setValues((prev) => ({
        ...prev,
        fk_appointment_type_id: String(newTypeId),
        fk_appointment_type_default_id: null,
      }));
      setSelectedDefaultId("new");
      setDefaultModifiedFields(new Set());
      syncLineItemsOfferFromSource({});
    }
  };

  const handleDefaultChange = async (e) => {
    const raw = e?.value;
    const defaultValue =
      raw === undefined || raw === null || raw === "" ? "new" : String(raw);
    if (isProgrammaticDefaultChange.current && defaultValue === "parent-applied") {
      isProgrammaticDefaultChange.current = false;
      return;
    }
    setSelectedDefaultId(defaultValue);

    if (defaultValue === "new") {
      lastAppliedChildDefaultRef.current = null;
      if (valuesBeforeDefaultFill) {
        const { _sendEmail, ...prev } = valuesBeforeDefaultFill;
        formik.setValues((cur) => ({
          ...cur,
          ...prev,
          fk_appointment_type_default_id: null,
        }));
        if (_sendEmail !== undefined) setSendEmail(_sendEmail);
      } else {
        formik.setValues((cur) => ({ ...cur, fk_appointment_type_default_id: null }));
      }
      setDefaultModifiedFields(new Set());
      setPendingApptTypeLineItems(null);
      setApptTypeLineItemsAdded(false);
      setApptTypeItemTargetId(null);
      setApptTypeLineItemSource(null);
      return;
    }

    if (defaultValue === "parent-applied") {
      lastAppliedChildDefaultRef.current = null;
      if (selectedType) {
        const currentVals = valuesBeforeDefaultFill
          ? { ...valuesBeforeDefaultFill }
          : { ...formik.values };
        delete currentVals._sendEmail;
        const savedEmail = valuesBeforeDefaultFill?._sendEmail ?? sendEmail;
        await applySource(
          selectedType,
          currentVals,
          savedEmail,
          { fk_appointment_type_default_id: null }
        );
      }
      return;
    }

    if (!selectedType) {
      return;
    }
    const childDefault = (selectedType.defaults || []).find(
      (d) => String(d.id) === String(defaultValue)
    );
    if (!childDefault) {
      return;
    }

    if (lastAppliedChildDefaultRef.current === String(defaultValue)) {
      return;
    }
    lastAppliedChildDefaultRef.current = String(defaultValue);

    const currentVals = {
      ...formik.values,
      fk_appointment_type_default_id: String(defaultValue),
    };
    const savedEmail = valuesBeforeDefaultFill?._sendEmail ?? sendEmail;
    await applySource(
      childDefault,
      currentVals,
      savedEmail,
      { fk_appointment_type_default_id: String(defaultValue) }
    );

    // If child has no line items but parent does, offer parent's template
    if (!childDefault.item_target_id && selectedType.item_target_id) {
      syncLineItemsOfferFromSource(selectedType);
    }
  };

  const revertField = (fieldName) => {
    if (!valuesBeforeDefaultFill) return;
    if (fieldName === "sendEmail") {
      setSendEmail(valuesBeforeDefaultFill._sendEmail);
    } else {
      formik.setFieldValue(fieldName, valuesBeforeDefaultFill[fieldName]);
    }
    setDefaultModifiedFields((prev) => {
      const next = new Set(prev);
      next.delete(fieldName);
      return next;
    });
  };

  const FieldLabel = ({ text, fieldName }) => {
    const isModified = defaultModifiedFields.has(fieldName);
    const [hovered, setHovered] = useState(false);
    return (
      <label>
        {isModified && (
          <TooltippedElement
            message={tr("Undo")}
            className="mt-[5px]"
            element={
              <button
                type="button"
                onMouseEnter={() => setHovered(true)}
                onMouseLeave={() => setHovered(false)}
                onClick={() => revertField(fieldName)}
              >
                {hovered
                  ? <CgUndo size={20} className="text-blue-500" />
                  : <IoMdInformationCircleOutline size={20} className="text-orange-400" />}
              </button>
            }
          />
        )}
        {text}
      </label>
    );
  };

  const handleAddToDescription = () => {
    const responsesString = responses
      .map((response, index) => {
        return `${index + 1}: ${response.question_label}\n${response.options
          .map((option) => option.response)
          .join("\n")}`;
      })
      .join("\n");
    formik.setFieldValue("note", formik.values.note + "\n\n" + responsesString);
  };

  useEffect(() => {
    setSendEmail(appointment?.is_booked ? false : true);
    getAppointmentEmailConfigMutation.mutate();
  }, []);

  // Stable reference required: enableReinitialize would reset the form every render
  // if this object were recreated each time, wiping type-default auto-fill.
  const initialValues = useMemo(() => {
    const existingAppointmentDate = parseIsoDateWithinRange(
      appointment?.date ?? null
    );
    return {
      date: existingAppointmentDate
        ? format(existingAppointmentDate, "yyyy-MM-dd")
        : "",
      start_time_utc: appointment?.start_time_utc
        ? convertFromUTC(appointment.start_time_utc, timezone, false)
        : "",
      end_time_utc: appointment?.end_time_utc
        ? convertFromUTC(appointment.end_time_utc, timezone, false)
        : "",
      duration: parseDuration(appointment?.duration).fullStr || "",
      assignee: appointment?.assignee || [],
      note: appointment?.note || "",
      estimate_amount:
        appointment?.estimate_amount != null ? appointment.estimate_amount / 100 : "",
      completion_note: appointment?.completion_note || "",
      type: appointment?.type === "request" ? "job" : appointment?.type || "job",
      fk_appointment_type_id:
        appointment?.fk_appointment_type_id?.toString() || "",
      fk_appointment_type_default_id:
        appointment?.fk_appointment_type_default_id?.toString() || null,
      estimate_products: null,
      estimate_pricetype: "sale",
      is_reminding: isBookit
        ? appointment?.is_booked
          ? true
          : false
        : appointment?.is_reminding || false,
    };
  }, [appointment, timezone, isBookit]);

  const onSubmit = (e) => {
    const parsedSubmitDate = parseIsoDateWithinRange(e.date ?? null);

    if (e.date && !parsedSubmitDate) {
      showSnackbar(invalidDateMessage);
      return;
    }

    const tempDate = parsedSubmitDate
      ? format(parsedSubmitDate, "yyyy-MM-dd")
      : format(new Date(), "yyyy-MM-dd");

    const formatData = mapValues(e, (value, key) => {
      if (key === "duration") {
        return toApiDurationString(value);
      }
      if (!value && value !== false) return null;
      if (key === "start_time_utc") {
        return convertToUTC(tempDate, value, timezone);
      }
      if (key === "end_time_utc") {
        return convertToUTC(tempDate, value, timezone);
      }
      return value;
    });
    const shouldApplyApptTypeLineItems =
      apptTypeLineItemsAdded && apptTypeItemTargetId != null;
    const apptTypeLineItemsPayload = shouldApplyApptTypeLineItems
      ? {
          appt_type_item_target_id: apptTypeItemTargetId,
        }
      : {};

    if (appointment) {
      console.log("updateAppt.mutate", {
        ...formatData,
        ...apptTypeLineItemsPayload,
        notify: sendEmail && appointmentEmailConfig,
        isBookit,
        assignee: e.assignee,
      });
      updateAppt.mutate({
        ...formatData,
        ...apptTypeLineItemsPayload,
        notify: sendEmail && appointmentEmailConfig,
        isBookit,
        assignee: e.assignee,
      });
    } else {
      if (mdatelist.length >= 1) {
        console.log("createNewAppt.mutateAsync", {
          ...formatData,
          ...apptTypeLineItemsPayload,
          parentId,
          parentType,
          parts: [],
          notify: sendEmail && appointmentEmailConfig,
          parent_app: null,
        });
        createNewAppt
          .mutateAsync({
            ...formatData,
            ...apptTypeLineItemsPayload,
            parentId,
            parentType,
            parts: [],
            notify: sendEmail && appointmentEmailConfig,
            parent_app: null,
          })
          .then((res) => {
            mdatelist.forEach((a) => {
              const multiDayDate = format(a.toDate(), "yyyy-MM-dd");
              const multiDayFormatData = {
                ...formatData,
                start_time_utc: convertToUTC(multiDayDate, e.start_time_utc, timezone),
                end_time_utc: convertToUTC(multiDayDate, e.end_time_utc, timezone),
              };
              delete multiDayFormatData.date;
              createNewAppt.mutate({
                ...multiDayFormatData,
                ...apptTypeLineItemsPayload,
                parentId,
                parentType,
                parts: [],
                notify: false,
                parent_app: res.id,
                date: a.toDate().toDateString(),
              });
            });
          })
          .then(() => {
            setModal();
          });
      } else {
        console.log("createNewAppt.mutateAsync", {
          ...formatData,
          ...apptTypeLineItemsPayload,
          parentId,
          parentType,
          parts: [],
          notify: sendEmail && appointmentEmailConfig,
          parent_app: null,
        });
        createNewAppt
          .mutateAsync({
            ...formatData,
            ...apptTypeLineItemsPayload,
            parentId,
            parentType,
            parts: [],
            notify: sendEmail && appointmentEmailConfig,
            parent_app: null,
          })
          .then(() => {
            setModal();
          });
      }
    }
  };

  const validationSchema = Yup.object().shape({
    date: Yup.string()
      .nullable()
      .test(
        "valid-date",
        invalidDateMessage,
        (value) => !value || isIsoDateWithinSupportedRange(value)
      ),
    start_time_utc: Yup.mixed().nullable(),
    end_time_utc: Yup.mixed().nullable(),
    duration: Yup.string().nullable(),
    tech_assigned: Yup.array().of(Yup.number()),
    note: Yup.string(),
    completion_note: Yup.string(),
    type: Yup.string(),
    fk_appointment_type_id: Yup.string().nullable(),
    fk_appointment_type_default_id: Yup.string().nullable(),
    is_reminding: Yup.boolean(),
  });

  const [Enddate, setEnddate] = React.useState(() => "none");
  const [mdatelist, setmdatelist] = React.useState(() => []);

  const switchMulti = () => {
    setEnddate((s) => (s === "none" ? "inline-block" : "none"));
  };

  const formik = useFormik({
    validationSchema,
    initialValues,
    onSubmit,
    enableReinitialize: true,
  });

  return (
    <AppointmentModal onSubmit={formik.handleSubmit}>
      <SHeader>
        <div className="w-[100%] flex flex-row items-center justify-between">
          <h2>{tr("Info")}</h2>
          <div
            size="small"
            className="text-blue-500 underline hover:text-blue-700"
          >
            <NoStyleButton
              size="small"
              className=""
              onClick={handleShowRequestData}
            >
              <span className="text-blue-500 underline hover:text-blue-700">
                {isFlyoutVisible ? "Hide" : "Show"} {tr("Responses")}
              </span>
            </NoStyleButton>
            {isFlyoutVisible && (
              <div className="absolute right-0 z-10 p-2 mt-2 bg-white border rounded shadow-lg min-w-[400px] text-black">
                <div className="p-4 text-lg font-extrabold uppercase">
                  {tr("Responses")}
                </div>
                <hr />
                <div className="overflow-y-scroll min-h-[200px] cursor-text">
                  {responses ? (
                    responses?.map((response, index) => {
                      return (
                        <div key={response.id} className="mb-4">
                          <div className="font-bold">
                            {index + 1}: {response.question_label}
                          </div>
                          {response &&
                          response?.options &&
                          response.options.length > 0 ? (
                            response.options.map((option, index) => (
                              <div key={index} className="ml-2">
                                {option.response}
                              </div>
                            ))
                          ) : (
                            <div>{tr("No response")}</div>
                          )}
                        </div>
                      );
                    })
                  ) : (
                    <div>{tr("No responses")}</div>
                  )}
                </div>
                <Button onClick={handleAddToDescription} primary>
                  {tr("Add to Description")}
                </Button>
              </div>
            )}
          </div>
        </div>
      </SHeader>
      <SContent>
        {!has_est && !appointment && formik.values.type === "job" && (
          <div className="flex p-2 my-2 text-yellow-600 border border-yellow-600 rounded bg-yellow-100/50">
            <VscWarning style={{ fontSize: "1.5rem", marginRight: "1rem" }} />
            <p>
              {tr("No estimate has marked as won yet. To get an accurate production value, please mark an estimate as won before proceeding to create appointments.")}
            </p>
          </div>
        )}

        <Row>
          <FieldLabel text={tr("Category")} fieldName="type" />
          <GroupedButton>
            <OutlineButton
              onClick={() => formik.setFieldValue("type", "estimate")}
              $selected={formik.values.type === "estimate"}
            >
              {tr("Estimate")}
            </OutlineButton>
            <OutlineButton
              onClick={() => formik.setFieldValue("type", "job")}
              $selected={formik.values.type === "job"}
            >
              {tr("Job")}
            </OutlineButton>
          </GroupedButton>
        </Row>
        <Row>
          <label>{tr("Type")}</label>
          <div className="flex gap-2">
            <AppointmentType
              className="!min-w-[200px]"
              value={formik.values.fk_appointment_type_id}
              onChange={handleTypeChange}
              isClearable
            />
            {selectedTypeId && defaultOptions.length > 1 && (
              <Field
                type="select"
                className="!min-w-[200px]"
                options={defaultOptions}
                value={selectedDefaultOption}
                onChange={handleDefaultChange}
                placeholder={tr("Select default...")}
              />
            )}
            {selectedTypeId &&
            (checklistDefaultsCountQuery.isLoading ||
              checklistDisplayCount > 0 ||
              (checklistDefaultsCountQuery.data ?? 0) > 0) ? (
              <span className="px-2 py-0.5 text-xs bg-blue-100 text-blue-700 rounded-full whitespace-nowrap self-center">
                {checklistDefaultsCountQuery.isLoading && checklistDisplayCount < 1
                  ? tr("Loading ellipsis")
                  : t(`${tBase}.Checklist defaults count`, {
                      count:
                        checklistDisplayCount > 0
                          ? checklistDisplayCount
                          : checklistDefaultsCountQuery.data ?? 0,
                    })}
              </span>
            ) : null}
          </div>
        </Row>
        <Row>
          <FieldLabel text={tr("Date")} fieldName="date" />
          <Input type="date" name="date" placeholder={"Date"} formik={formik} />
        </Row>
        <Row>
          <label>{tr("Multi day appointment")}</label>
          <Switch onChange={switchMulti} />
          <label style={{ display: Enddate }}>{tr("Enter Other Dates")}</label>
          <DatePicker
            multiple
            style={{
              display: Enddate,
              width: "100%",
              height: "40px",
              background: "rgba(0,0,0,0)",
              border: "1px solid #d9d9d9",
              padding: "0 1em",
            }}
            value={mdatelist}
            onChange={setmdatelist}
          />
        </Row>
        <Row>
          <FieldLabel text={tr("Arrival Time Window")} fieldName="start_time_utc" />
          <TimeCon>
            <Input
              type="time"
              name="start_time_utc"
              placeholder={tr("Start Time")}
              formik={formik}
            />
            <span style={{ margin: "0 0.5rem 0 0.5rem" }}>{"-"}</span>
            <Input
              type="time"
              name="end_time_utc"
              placeholder={tr("End Time")}
              formik={formik}
            />
          </TimeCon>
        </Row>
        <Row>
          <FieldLabel text={tr("Duration")} fieldName="duration" />
          <DurationInput name="duration" formik={formik} />
        </Row>
        <Row>
          <FieldLabel text={tr("Assign to")} fieldName="assignee" />
          <AssigneeSelect
            assigneeType="appointment"
            name="assignee"
            value={formik.values.assignee}
            onChange={(e) => {
              formik.setValues((v) => ({ ...v, assignee: e }));
            }}
          />
        </Row>
        <Row>
          <FieldLabel text={tr("Description")} fieldName="note" />
          <Field name="note" type="textarea" minRows={4} formik={formik} />
        </Row>
        <Row>
          <FieldLabel text={tr("Completion Note")} fieldName="completion_note" />
          <Field
            name="completion_note"
            type="textarea"
            minRows={4}
            formik={formik}
          />
        </Row>
      </SContent>

      <SContent>
        <AppointmentModalChecklistsSection
          ref={checklistSectionRef}
          appointmentId={appointmentId ? Number(appointmentId) : null}
          fkAppointmentTypeId={formik.values.fk_appointment_type_id ?? ""}
          fkAppointmentTypeDefaultId={formik.values.fk_appointment_type_default_id ?? null}
          selectedDefaultMode={selectedDefaultId ?? null}
          hasSubAppointmentDefaultPicker={defaultOptions.length > 1}
          isCompleted={appointment?.status?.label === "Completed"}
          onDisplayCountChange={setChecklistDisplayCount}
        />
      </SContent>

      {/* Default line items from appointment type: optional until user clicks Add + */}
      <SHeader
        style={{
          justifyContent: "flex-start",
          flexWrap: "wrap",
          alignItems: "center",
          gap: "1rem",
        }}
      >
        <h2 style={{ margin: 0 }}>{tr("Line Items for Appointment")}</h2>
        {pendingApptTypeLineItems && !apptTypeLineItemsAdded && (pendingLineItemsCountQuery.isLoading || pendingLineItemsOfferCount > 0) && (
          <div className="flex flex-wrap items-center gap-2">
            <Button
              type="button" 
              primary
              onClick={() => {
                void handleAddAppointmentTypeLineItems();
              }}
              disabled={
                pendingLineItemsCountQuery.isLoading
                || pendingLineItemsOfferCount < 1
                || isLineItemsPrefetching
              }
            >
              {tr("Add plus")}
            </Button>
            <span className="text-sm text-gray-600">
              {pendingLineItemsCountQuery.isLoading
                ? tr("Loading ellipsis")
                : t(`${tBase}.Appt type line items offer`, {
                    count: pendingLineItemsOfferCount,
                    label: selectedType?.label || "",
                  })}
            </span>
          </div>
        )}
      </SHeader>
      <SContent>
        {appointment && appointmentId ? (
          <LineItemsContainer
            key={`appointment-lineitems-${appointmentId}`}
            view={apptTypeLineItemsAdded ? "edit" : "read"}
            onCancel={handleAppointmentLineItemsCancel}
            type="appointment"
            id={String(appointmentId)}
            parentId={id}
            parentType={type}
            noTax={true}
          />
        ) : (
          <LineItemsContainer
            key={
              apptTypeLineItemSource && apptTypeLineItemsAdded
                ? `${apptTypeLineItemSource.lineItemsType}-${apptTypeLineItemSource.lineItemsId}`
                : "appointment-type-lineitems-empty"
            }
            view={apptTypeLineItemsAdded ? "edit" : "read"}
            type={apptTypeLineItemSource?.lineItemsType ?? "appointment-type"}
            id={
              apptTypeLineItemSource && apptTypeLineItemsAdded
                ? apptTypeLineItemSource.lineItemsId
                : "new"
            }
            parentId={
              apptTypeLineItemSource && apptTypeLineItemsAdded
                ? apptTypeLineItemSource.parentId
                : undefined
            }
            noTax={true}
          />
        )}
      </SContent>

      {!appointment && (
        <>
          <SHeader>
            <h2>{tr("Estimate")}</h2>
            <Switch
              style={{ marginLeft: "auto" }}
              checked={isEstimating}
              onChange={setIsEstimating}
            />
          </SHeader>
          <SContent>
            {isEstimating && (
              <LineItemsContainer
                view="edit"
                type="estimate_appt"
                parentType={type}
                id={id}
                onClick={(p) => {
                  formik.setFieldValue("estimate_products", p.products);
                  formik.setFieldValue("estimate_pricetype", p.price_type);
                }}
                noTax={true}
              />
            )}
          </SContent>
        </>
      )}
      <div
        className="space-x-4"
        style={{
          display: "flex",
          justifyContent: "flex-end",
          alignItems: "center",
        }}
      >
        {type !== "projects" && (
          <>
            <div className="space-x-2">
              <span>{tr("Send Reminder")}</span>
              <Switch
                checked={formik.values.is_reminding}
                onChange={(e) => formik.setFieldValue("is_reminding", e)}
              />
            </div>
            <div className="space-x-2">
              <span style={{ color: "grey", fontSize: "85%" }}>
                {tr("Send E-mail Confirmation to Customer")}
              </span>
              <Switch
                checked={sendEmail && appointmentEmailConfig}
                onChange={(e) => {
                  setSendEmail(e);
                  setAppointmentEmailConfig(e);
                }}
              />
            </div>
          </>
        )}
        <Button
          type="submit"
          primary
          isLoading={createNewAppt.isLoading || updateAppt.isLoading}
        >
          {isBookit
            ? appointment?.is_booked
              ? tr("Submit")
              : "Book It"
            : tr("Submit")}
        </Button>
      </div>
    </AppointmentModal>
  );
}

const SHeader = styled.div`
  display: flex;
  align-items: center;
  border-bottom: 1px solid ${theme.border.dark};
  padding: 1rem 0;
`;

const SContent = styled.div`
  display: flex;
  flex-direction: column;
`;
